import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";

const SEV_COLOR = {
    critical: "bg-red-500",
    high: "bg-orange-500",
    medium: "bg-amber-400",
    low: "bg-slate-400",
};

const STATUS_STYLES = {
    open: "bg-red-500/10 text-red-600 border-red-500/30",
    ack: "bg-amber-500/10 text-amber-600 border-amber-500/30",
    snoozed: "bg-slate-500/10 text-slate-600 border-slate-500/30",
    resolved: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
};

export default function Alerts() {
    const [alerts, setAlerts] = useState([]);
    const [status, setStatus] = useState("All");

    async function load() {
        const params = status === "All" ? {} : { status };
        const { data } = await http.get("/alerts", { params });
        setAlerts(data);
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    async function updateStatus(id, next) {
        try {
            const { data } = await http.patch(`/alerts/${id}`, { status: next });
            setAlerts((prev) => prev.map((a) => (a.id === id ? data : a)));
            toast.success(`Alert marked ${next}`);
        } catch (e) {
            toast.error("Update failed");
        }
    }

    return (
        <div className="mx-auto max-w-[1560px] space-y-4 p-6">
            <div className="flex items-end justify-between">
                <div>
                    <div className="mlabel">Workflow</div>
                    <h1 className="text-[22px] font-semibold tracking-tight">Alerts & Action Queue</h1>
                </div>
                <div className="text-[11px] text-muted-foreground">{alerts.length} alerts</div>
            </div>

            <div className="flex items-center gap-1 rounded-sm border border-border bg-card p-0.5 w-fit">
                {["All", "open", "ack", "resolved", "snoozed"].map((s) => (
                    <button
                        key={s}
                        data-testid={`alert-status-${s}`}
                        onClick={() => setStatus(s)}
                        className={
                            "rounded-sm px-2.5 py-1 text-[11px] font-medium capitalize transition-colors " +
                            (status === s
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground hover:text-foreground")
                        }
                    >
                        {s}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="alerts-grid">
                {alerts.map((a) => (
                    <div
                        key={a.id}
                        data-testid={`alerts-page-${a.id}`}
                        className="hover-lift flex flex-col gap-2 rounded-sm border border-border bg-card p-4"
                    >
                        <div className="flex items-center gap-2">
                            <span className={`h-2 w-2 rounded-full ${SEV_COLOR[a.severity]}`} />
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                {a.category.replace("_", " ")}
                            </span>
                            <span
                                className={
                                    "ml-auto rounded-sm border px-1.5 py-[1px] text-[10px] font-medium " +
                                    STATUS_STYLES[a.status]
                                }
                            >
                                {a.status}
                            </span>
                        </div>
                        <div className="text-[13px] font-semibold">{a.title}</div>
                        <div className="text-[12px] text-muted-foreground">{a.detail}</div>

                        {a.ai_reasoning && (
                            <div className="rounded-sm border-l-2 border-[hsl(var(--ai))] bg-[hsl(var(--ai))]/[0.05] p-2">
                                <div className="mlabel mb-0.5 text-[hsl(var(--ai))]">Copilot Reasoning</div>
                                <div className="text-[11px] leading-relaxed">{a.ai_reasoning}</div>
                                {a.ai_confidence != null && (
                                    <div className="mt-1 flex items-center gap-2">
                                        <div className="conf-bar flex-1">
                                            <span style={{ width: `${Math.round(a.ai_confidence * 100)}%` }} />
                                        </div>
                                        <span className="mono-num text-[10px]">
                                            {Math.round(a.ai_confidence * 100)}%
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="mt-1 flex items-center gap-2 text-[11px]">
                            {a.owner && (
                                <span className="text-muted-foreground">
                                    Owner: <span className="text-foreground">{a.owner}</span>
                                </span>
                            )}
                            <div className="ml-auto flex gap-1">
                                {a.status !== "ack" && (
                                    <button
                                        onClick={() => updateStatus(a.id, "ack")}
                                        data-testid={`ack-${a.id}`}
                                        className="rounded-sm border border-border bg-background px-2 py-0.5 text-[11px] hover:border-foreground/30"
                                    >
                                        Acknowledge
                                    </button>
                                )}
                                {a.status !== "resolved" && (
                                    <button
                                        onClick={() => updateStatus(a.id, "resolved")}
                                        data-testid={`resolve-${a.id}`}
                                        className="rounded-sm bg-primary px-2 py-0.5 text-[11px] text-primary-foreground hover:bg-primary/90"
                                    >
                                        Resolve
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
